import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { decodeRpfString } from "./ProfilePointUtils";

import ProfilePointsChart from "./ProfilePointsChart";
import { IDEAL_PROFILE_RECORD } from "./idealProfiles";
import { geometryDataSelector } from "./utils";

export const SIDE_LEFT = "LEFT PROFILE";
export const SIDE_RIGHT = "RIGHT PROFILE";

const ProfilePoints = ({ settersRef, chartHandleRef, side }) => {
    //rpf flag isLeft so 0 => right
    const geometryData = useSelector(geometryDataSelector);
    const chartContainerRef = useRef(null);
    const [index, setIndex] = useState(0);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const canvasOptions = useMemo(() => {
        return {
            padding: {
                left: 35,
                right: 12,
                top: 12,
                bottom: 35,
            },
            coordinates: {
                startX: 40,
                endX: 220,
                startY: 90,
                endY: 270,
            },
            axes: {
                step: 20,
                transformationX: side === SIDE_LEFT ? (x) => -1 * x + (220 + 40) : (x) => x,
            },
        };
    }, [side]);

    const points = useMemo(() => {
        const geometryDatum = geometryData[index];
        const key = side === SIDE_LEFT ? "left_profile_point_array" : "right_profile_point_array";

        if (!geometryDatum || !geometryDatum[key]) return null;

        const points = decodeRpfString(geometryDatum[key]);

        return points;
    }, [geometryData, index, side]);

    const profileType = useMemo(() => {
        const geometryDatum = geometryData[index];
        const key = side === SIDE_LEFT ? "left_profile_rail_type" : "right_profile_rail_type";

        if (!geometryDatum) return null;

        const type = geometryDatum[key];

        if (!type) return null;

        return type;
    }, [geometryData, index, side]);

    const idealProfile = useMemo(() => {
        if (!profileType) return null;

        return IDEAL_PROFILE_RECORD[profileType];
    }, [profileType]);

    const transformation = useMemo(() => {
        const geometryDatum = geometryData[index];
        const key = side === SIDE_LEFT ? "left_profile_transform" : "right_profile_transform";

        if (!geometryDatum || !geometryDatum[key]) return { dx: 0, dy: 0, angle: 0 };

        return geometryDatum[key];
    }, [geometryData, index, side]);

    useEffect(() => {
        const _settersRef = settersRef.current;
        const _handlersRef = chartHandleRef.current;

        _settersRef[side] = setIndex;
        _handlersRef[side] = setIndex;

        return () => {
            _settersRef[side] = null;
            _handlersRef[side] = null;
        };
    }, [chartHandleRef, settersRef, side]);

    useLayoutEffect(() => {
        if (!chartContainerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;

                const size = Math.min(width, height); // must be square for now

                // This seems to leave some flicker with just useEffect
                // This + LayoutEffect seems to completely resolve it?
                requestAnimationFrame(() => {
                    setDimensions({ width: size, height: size });
                });
            }
        });

        resizeObserver.observe(chartContainerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div className="PointChart">
            <div
                className="w-full flex justify-between items-center"
                style={{ minHeight: 40 }}>
                <strong>{side}</strong>
                {profileType && <span className="ProfileType">{profileType}</span>}
            </div>
            <div
                style={{ flex: 1, overflow: "hidden" }}
                ref={chartContainerRef}>
                <ProfilePointsChart
                    width={dimensions.width}
                    height={dimensions.height}
                    points={points}
                    transformation={transformation}
                    idealProfile={idealProfile}
                    canvasOptions={canvasOptions}
                />
            </div>
        </div>
    );
};

export default ProfilePoints;
